import { useEffect, useMemo, useRef } from 'react';
import { useSearchMoratoriums } from './useSearchQuoteMoratoriums';
import { useQuoteStore } from '@store/Quote/QuoteStore';
import { shallow } from 'zustand/shallow';
import { useStore } from '@store/GlobalStore';
import { AlertStatus } from '@models/alert';
import { CodeName } from '@models/quoteItem';
import { isNotProduction } from '@utils/isInLowerEnv';

type MoratoriumBannerData = {
  message: string;
  severity: AlertStatus;
} | null;

export const useQuoteMoratoriums = (quoteId: string) => {
  const { isInternalUser } = useStore((state) => state.userAuth, shallow);
  const [structuredQuote] = useQuoteStore((state) => [state.structuredQuote], shallow);

  // Get LOIPropertyInd value from policy quote item
  const loiPropertyInd = useMemo(() => {
    if (!structuredQuote) return false;
    const policyQuoteItem = structuredQuote.quoteItems.find((qi) => qi.quoteItemType.codeName === CodeName.Policy);
    return policyQuoteItem?.attributeTypes?.LOIPropertyInd?.attribute?.value === true;
  }, [structuredQuote]);

  // Get policy dates for change detection
  const policyDates = useMemo(() => {
    if (!structuredQuote) return { effectiveDate: null, expirationDate: null };
    const policyQuoteItem = structuredQuote.quoteItems.find((qi) => qi.quoteItemType.codeName === CodeName.Policy);

    const effectiveDate =
      policyQuoteItem?.attributeTypes?.PolicyEffectiveDate?.attribute?.valueOverride ||
      policyQuoteItem?.attributeTypes?.PolicyEffectiveDate?.attribute?.value;
    const expirationDate =
      policyQuoteItem?.attributeTypes?.PolicyExpirationDate?.attribute?.valueOverride ||
      policyQuoteItem?.attributeTypes?.PolicyExpirationDate?.attribute?.value;

    return {
      effectiveDate: typeof effectiveDate === 'string' ? effectiveDate : null,
      expirationDate: typeof expirationDate === 'string' ? expirationDate : null,
    };
  }, [structuredQuote]);

  // Get counts for change detection
  const propertyRiskCount = useMemo(() => {
    if (!structuredQuote) return 0;
    return structuredQuote.quoteItems.filter((qi) => qi.quoteItemType.codeName === CodeName.PropertyRisk).length;
  }, [structuredQuote]);

  // Get all zip codes from location for change detection
  const addressZipCodes = useMemo(() => {
    if (!structuredQuote) return [];

    const relevantQuoteItems = structuredQuote.quoteItems.filter(
      (qi) => qi.quoteItemType.codeName === CodeName.QuoteLocation,
    );

    return relevantQuoteItems.map((qi) => {
      const zipCodeValue =
        qi.attributeTypes?.AddressZip?.attribute?.valueOverride || qi.attributeTypes?.AddressZip?.attribute?.value;

      return {
        id: qi.id,
        zipCode: typeof zipCodeValue === 'string' || typeof zipCodeValue === 'number' ? String(zipCodeValue) : null,
      };
    });
  }, [structuredQuote]);

  // Only fetch moratoriums if LOIPropertyInd is true
  const {
    data: moratoriumData,
    refetch,
    isLoading,
    error,
  } = useSearchMoratoriums(loiPropertyInd ? quoteId : undefined);

  // Track previous values for change detection
  const prevLoiPropertyInd = useRef<boolean | null>(null);
  const prevPropertyRiskCount = useRef<number | null>(null);
  const prevPolicyDates = useRef<{ effectiveDate: string | null; expirationDate: string | null }>({
    effectiveDate: null,
    expirationDate: null,
  });
  const prevAddressZipCodes = useRef<Array<{ id: string; zipCode: string | null }>>([]);

  // Auto-refetch when relevant quote data changes
  useEffect(() => {
    // Check LOIPropertyInd changes (refetch when it becomes true)
    if (prevLoiPropertyInd.current !== null && prevLoiPropertyInd.current !== loiPropertyInd && loiPropertyInd) {
      if (isNotProduction()) {
        console.debug('LOIPropertyInd changed to true, refetching moratoriums');
      }
      refetch();
    }
    prevLoiPropertyInd.current = loiPropertyInd;
  }, [loiPropertyInd, refetch]);

  useEffect(() => {
    // Check property risk changes
    if (
      loiPropertyInd &&
      prevPropertyRiskCount.current !== null &&
      prevPropertyRiskCount.current !== propertyRiskCount
    ) {
      if (isNotProduction()) {
        console.debug('Property risk count change detected, refetching moratoriums');
      }
      refetch();
    }
    prevPropertyRiskCount.current = propertyRiskCount;
  }, [propertyRiskCount, loiPropertyInd, refetch]);

  useEffect(() => {
    // Check policy date changes
    if (
      loiPropertyInd &&
      prevPolicyDates.current.effectiveDate !== null &&
      (prevPolicyDates.current.effectiveDate !== policyDates.effectiveDate ||
        prevPolicyDates.current.expirationDate !== policyDates.expirationDate)
    ) {
      if (isNotProduction()) {
        console.debug('Policy date change detected, refetching moratoriums');
      }
      refetch();
    }
    prevPolicyDates.current = policyDates;
  }, [policyDates, loiPropertyInd, refetch]);

  useEffect(() => {
    // Check zip code changes on location and property risk items
    if (loiPropertyInd && prevAddressZipCodes.current.length > 0) {
      // Compare current zip codes with previous ones
      const hasZipCodeChanged =
        prevAddressZipCodes.current.length !== addressZipCodes.length ||
        prevAddressZipCodes.current.some((prev) => {
          const current = addressZipCodes.find((curr) => curr.id === prev.id);
          return !current || current.zipCode !== prev.zipCode;
        });

      if (hasZipCodeChanged) {
        if (isNotProduction()) {
          console.debug('Zip code change detected, refetching moratoriums');
        }
        refetch();
      }
    }
    prevAddressZipCodes.current = addressZipCodes;
  }, [addressZipCodes, loiPropertyInd, refetch]);

  // Determine banner data based on moratorium response
  const bannerData: MoratoriumBannerData = useMemo(() => {
    if (!moratoriumData?.items?.length) return null;

    const moratoriums = moratoriumData.items;

    // Check if any moratorium is active (resolvedAt is null/undefined)
    const hasActiveMoratorium = moratoriums.some((moratorium) => !moratorium.resolvedAt);

    if (hasActiveMoratorium) {
      return {
        message: 'This location is under an active moratorium. Please contact your underwriter for assistance.',
        severity: AlertStatus.Warning,
      };
    }

    // All moratoriums are historical (all have resolvedAt populated)
    const allResolved = moratoriums.every((moratorium) => moratorium.resolvedAt);

    if (allResolved && isInternalUser) {
      return {
        message: 'This quote spans dates that overlap with a moratorium that has since been resolved.',
        severity: AlertStatus.Warning,
      };
    }

    return null;
  }, [moratoriumData, isInternalUser]);

  return {
    moratoriums: moratoriumData?.items || [],
    bannerData,
    refetch,
    isLoading,
    error,
    shouldShowBanner: !!bannerData,
    loiPropertyInd,
  };
};
